import type { User } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { supabase } from '@/lib/supabase';

/**
 * Returns the current Supabase user session
 * Used for protected routes
 */
export const useSession = () => {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		// Get initial session
		supabase.auth.getSession().then(({ data: { session } }) => {
			setUser(session?.user ?? null);
			setLoading(false);
		});

		// Listen for auth changes
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((_event, session) => {
			setUser(session?.user ?? null);
			setLoading(false);
		});

		return () => subscription.unsubscribe();
	}, []);

	// Return null while loading to prevent flash of login page
	if (loading) return undefined;
	return user;
};

/**
 * Returns auth state with initializing flag
 * Used by GlobalContext
 */
export const useAuth = () => {
	const [authState, setAuthState] = useState<{
		initializing: boolean;
		user: User | null;
	}>({
		initializing: true,
		user: null,
	});

	useEffect(() => {
		// Get initial session
		supabase.auth.getSession().then(({ data: { session } }) => {
			setAuthState({
				initializing: false,
				user: session?.user ?? null,
			});
		});

		// Listen for auth changes
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((_event, session) => {
			setAuthState({
				initializing: false,
				user: session?.user ?? null,
			});
		});

		return () => subscription.unsubscribe();
	}, []);

	return authState;
};

/**
 * Returns the current user's role from the database
 */
export const useUserRole = (refetchOnMount: boolean | 'always' = true) => {
	return useQuery({
		queryKey: ['get-current-user-role'],
		queryFn: async () => {
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session?.user) return null;

			const { data, error } = await supabase
				.from('users')
				.select('user_role, user_id, user_email, school_id')
				.eq('user_id', session.user.id)
				.maybeSingle();

			if (error) {
				console.error('Error fetching user role:', error);
				return null;
			}

			return data;
		},
		staleTime: Infinity,
		refetchOnMount,
	});
};

/**
 * Hook to get current user from database
 */
/**
 * Hook to get current user from database
 */
export const useCurrentUser = (enabled = true) => {
	return useQuery({
		queryKey: ['get-current-user'],
		queryFn: async () => {
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session?.user) return null;

			const { data, error } = await supabase
				.from('users')
				.select('*')
				.eq('user_id', session.user.id)
				.maybeSingle();

			if (error) {
				console.error('Error fetching current user:', error);
				return null;
			}

			if (!data) return null;

			// Fetch dept_chair user data if user has a dept_chair
			// Note: user_dept_chair stores Firebase UIDs, so we query by user_firebase_id
			let deptChair = null;
			if (data.user_dept_chair) {
				const { data: deptChairData, error: deptChairError } = await supabase
					.from('users')
					.select('user_id, user_email, user_first, user_last')
					.eq('user_firebase_id', data.user_dept_chair)
					.maybeSingle();

				if (!deptChairError && deptChairData) {
					deptChair = deptChairData;
				}
			}

			// Transform the data to include dept_chair object
			return {
				...data,
				dept_chair: deptChair,
			};
		},
		staleTime: Infinity,
		enabled,
	});
};
